export const SUB_OPTIMAL_FILLING_THRESHOLD = 65;
export const FILLING_RATE_OBJECTIVE = 80;

/** Provisional thresholds from PO review (17/07/2026) — to validate with Béatrice. */
export const HIGH_IMPACT_GAP_PERCENT = 10;
export const INSTABILITY_VARIATION_PERCENT = 10;
const MIN_ROTATIONS_FOR_GAP = 3;
const MIN_ROTATIONS_FOR_INSTABILITY = 3;
const MIN_PREVIOUS_COLLECTIONS_FOR_TREND = 10;
const PREVIOUS_PERIOD_DATA_COVERAGE_RATIO = 0.5;

const SEVERITY_ORDER = { high: 0, medium: 1, watch: 2 };

/**
 * Hide period-over-period trends when the previous window is empty,
 * shorter than the selected period, or only partially covered by data
 * (typical of sites that just started collecting).
 */
export const isPreviousPeriodComparable = ({
  enablePreviousPeriod,
  periodStart,
  periodStop,
  previousPeriodStart,
  previousPeriodStop,
  previousStatistics,
}) => {
  if (!enablePreviousPeriod || !previousStatistics) return false;

  const previousCount = previousStatistics.countCollections ?? 0;
  if (previousCount <= 0) return false;

  if (periodStart && periodStop && previousPeriodStart && previousPeriodStop) {
    const currentDuration =
      new Date(periodStop).getTime() - new Date(periodStart).getTime();
    const previousDuration =
      new Date(previousPeriodStop).getTime() -
      new Date(previousPeriodStart).getTime();
    if (
      !Number.isFinite(currentDuration) ||
      !Number.isFinite(previousDuration) ||
      currentDuration <= 0 ||
      previousDuration <= 0
    ) {
      return false;
    }
    // Same length expected; allow 1 day of clock/DST drift
    if (Math.abs(currentDuration - previousDuration) > 24 * 60 * 60 * 1000) {
      return false;
    }
  }

  const collections = previousStatistics.collectionsData;
  if (Array.isArray(collections) && collections.length > 0) {
    if (!previousPeriodStart || !previousPeriodStop) {
      return previousCount >= MIN_PREVIOUS_COLLECTIONS_FOR_TREND;
    }
    const periodDuration =
      new Date(previousPeriodStop).getTime() -
      new Date(previousPeriodStart).getTime();
    const times = collections
      .map((row) => new Date(row.datetime).getTime())
      .filter((time) => Number.isFinite(time));
    if (times.length === 0 || periodDuration <= 0) return false;
    const dataSpan = Math.max(...times) - Math.min(...times);
    return dataSpan >= periodDuration * PREVIOUS_PERIOD_DATA_COVERAGE_RATIO;
  }

  return previousCount >= MIN_PREVIOUS_COLLECTIONS_FOR_TREND;
};

export const computeAverageFillingRate = (dataResult) => {
  if (!dataResult) return 0;
  let sumFillingLevel = 0;
  let countFillingLevel = 0;
  Object.values(dataResult).forEach((flux) => {
    sumFillingLevel += flux?.sumFillingLevel ?? 0;
    countFillingLevel += flux?.countFillingLevel ?? 0;
  });
  return countFillingLevel > 0 ? sumFillingLevel / countFillingLevel : 0;
};

export const countSubOptimalRotations = (collectionsData) => {
  if (!Array.isArray(collectionsData)) return 0;
  return collectionsData.filter(
    (row) =>
      typeof row?.filling_level === "number" &&
      row.filling_level < SUB_OPTIMAL_FILLING_THRESHOLD,
  ).length;
};

const roundPercent = (value) => Math.round(Number(value) || 0);

const groupCollectionsByFlux = (collectionsData) => {
  const byFlux = {};
  collectionsData.forEach((row) => {
    if (typeof row?.filling_level !== "number") return;
    const label = row?.wasteCategoryLabel?.trim?.() || "";
    if (!label) return;
    if (!byFlux[label]) {
      byFlux[label] = [];
    }
    byFlux[label].push(row);
  });
  return byFlux;
};

const computeFluxAverage = (rows) => {
  if (!rows.length) return 0;
  const sum = rows.reduce((acc, row) => acc + row.filling_level, 0);
  return sum / rows.length;
};

/**
 * Max absolute filling variation between successive collections (same flux).
 */
const computeMaxSuccessiveVariation = (rows) => {
  if (!Array.isArray(rows) || rows.length < 2) return 0;
  const sorted = [...rows].sort(
    (a, b) => new Date(a.datetime).getTime() - new Date(b.datetime).getTime(),
  );
  let maxVariation = 0;
  for (let i = 1; i < sorted.length; i += 1) {
    const variation = Math.abs(
      sorted[i].filling_level - sorted[i - 1].filling_level,
    );
    if (variation > maxVariation) maxVariation = variation;
  }
  return maxVariation;
};

const buildGapRecommendation = ({ fluxLabel, average, gap, objective }) => {
  const roundedAvg = roundPercent(average);
  const roundedGap = roundPercent(gap);
  const isHigh = gap > HIGH_IMPACT_GAP_PERCENT;

  return {
    id: `gap-${isHigh ? "high" : "medium"}-${fluxLabel}`,
    severity: isHigh ? "high" : "medium",
    type: isHigh ? "under_objective_high" : "under_objective_medium",
    titleKey: isHigh
      ? "recommendation.underObjectiveHigh"
      : "recommendation.underObjectiveMedium",
    titleParams: { flux: fluxLabel },
    impactKey: "recommendation.gapToObjective",
    impactParams: {
      average: roundedAvg,
      objective,
      gap: roundedGap,
    },
    showAutoCollectionUpsell: false,
  };
};

const buildInstabilityRecommendation = ({
  fluxLabel,
  maxVariation,
  objective,
}) => ({
  id: `instability-${fluxLabel}`,
  severity: "watch",
  type: "instability",
  titleKey: "recommendation.instability",
  titleParams: { flux: fluxLabel },
  impactKey: "recommendation.instabilityImpact",
  impactParams: {
    variation: roundPercent(maxVariation),
    objective,
  },
  showAutoCollectionUpsell: true,
});

export const buildCollectionInsights = (statisticsCollectionCurrent) => {
  const emptyInsights = {
    subOptimalShare: 0,
    subOptimalCount: 0,
    dominantFlux: null,
    anomaly: null,
    recommendations: [],
  };

  const collectionsData = statisticsCollectionCurrent?.collectionsData;
  if (!Array.isArray(collectionsData) || collectionsData.length === 0) {
    return emptyInsights;
  }

  const rotationsWithFilling = collectionsData.filter(
    (row) => typeof row?.filling_level === "number",
  );
  const totalWithFilling = rotationsWithFilling.length;
  if (totalWithFilling === 0) {
    return emptyInsights;
  }

  const subOptimalCount = rotationsWithFilling.filter(
    (row) => row.filling_level < SUB_OPTIMAL_FILLING_THRESHOLD,
  ).length;
  const subOptimalShare = subOptimalCount / totalWithFilling;

  const byFlux = groupCollectionsByFlux(collectionsData);
  const recommendations = [];
  let dominantFlux = null;

  Object.entries(byFlux).forEach(([fluxLabel, rows]) => {
    const average = computeFluxAverage(rows);
    const gap = FILLING_RATE_OBJECTIVE - average;

    if (rows.length >= MIN_ROTATIONS_FOR_GAP && gap > 0) {
      const recommendation = buildGapRecommendation({
        fluxLabel,
        average,
        gap,
        objective: FILLING_RATE_OBJECTIVE,
      });
      recommendations.push(recommendation);

      if (
        !dominantFlux ||
        gap > dominantFlux.gap ||
        (gap === dominantFlux.gap && rows.length > dominantFlux.total)
      ) {
        dominantFlux = {
          label: fluxLabel,
          total: rows.length,
          average: roundPercent(average),
          gap: roundPercent(gap),
          severity: recommendation.severity,
        };
      }
    }

    if (rows.length >= MIN_ROTATIONS_FOR_INSTABILITY) {
      const maxVariation = computeMaxSuccessiveVariation(rows);
      if (maxVariation >= INSTABILITY_VARIATION_PERCENT) {
        recommendations.push(
          buildInstabilityRecommendation({
            fluxLabel,
            maxVariation,
            objective: FILLING_RATE_OBJECTIVE,
          }),
        );
      }
    }
  });

  recommendations.sort(
    (a, b) =>
      (SEVERITY_ORDER[a.severity] ?? 99) - (SEVERITY_ORDER[b.severity] ?? 99),
  );

  // Banner mirrors recommendation rules: top under-objective reco (high then medium).
  let anomaly = null;
  const topGapRecommendation = recommendations.find(
    (item) =>
      item.type === "under_objective_high" ||
      item.type === "under_objective_medium",
  );
  if (topGapRecommendation) {
    anomaly = {
      scope: "flux",
      type: topGapRecommendation.type,
      severity: topGapRecommendation.severity,
      fluxLabel: topGapRecommendation.titleParams.flux,
      average: topGapRecommendation.impactParams.average,
      objective: topGapRecommendation.impactParams.objective,
      gap: topGapRecommendation.impactParams.gap,
      share: topGapRecommendation.impactParams.gap,
      threshold: topGapRecommendation.impactParams.objective,
      subOptimalCount,
    };
  }

  return {
    subOptimalShare,
    subOptimalCount,
    dominantFlux,
    anomaly,
    recommendations,
  };
};
